import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import CustomEditor from '../../../Components/TextEditor/CustomEditor';
import AdressInput from '../../Atoms/AdressInput';

// Reuse theme from CreateEventModern
const theme = {
  colors: {
    primary: '#0010ff',
    primaryLight: '#3344ff',
    primaryDark: '#0008cc',
    secondary: '#7C3AED',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    eventTypes: {
      design: {
        primary: '#10B981',
        light: '#6EE7B7',
        dark: '#047857',
        background: '#ECFDF5'
      }
    },
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    },
    text: {
      primary: '#111827',
      secondary: '#374151',
      tertiary: '#6B7280'
    },
    border: {
      primary: '#E5E7EB',
      focus: '#3B82F6'
    }
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px'
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1)'
  }
};

// Styled components
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  background: ${theme.colors.gray[50]};
  min-height: calc(100vh - 200px);
`;

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xxl};
`;

const BackButton = styled.button`
  background: white;
  border: 2px solid ${theme.colors.gray[300]};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: ${theme.colors.gray[100]};
    border-color: ${theme.colors.eventTypes.design.primary};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
    color: ${theme.colors.eventTypes.design.dark};
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.eventTypes.design.primary};
  margin: 0 0 ${theme.spacing.md} 0;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  margin: 0;
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xxl};
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Card = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.md};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.gray[100]};
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.eventTypes.design.background};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  border: 2px solid ${theme.colors.eventTypes.design.light};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const InfoGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text.tertiary};
  margin-bottom: ${theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const Value = styled.div`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  font-weight: 500;
`;

const PreviewCard = styled(Card)`
  position: sticky;
  top: ${theme.spacing.xl};
  height: fit-content;
  border: 2px solid ${theme.colors.eventTypes.design.primary};
`;

const PreviewTitle = styled.h3`
  color: ${theme.colors.eventTypes.design.primary};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  background: ${props => {
    switch(props.status) {
      case 'active': return theme.colors.success;
      case 'upcoming': return theme.colors.warning;
      case 'past': return theme.colors.gray[400];
      default: return theme.colors.gray[200];
    }
  }};
  color: white;
`;

const ActionButton = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: linear-gradient(135deg, ${theme.colors.eventTypes.design.primary} 0%, ${theme.colors.eventTypes.design.dark} 100%);
  color: white;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MediaContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const MediaItem = styled.div`
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
  }
  
  .placeholder {
    width: 100%;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.colors.gray[100]};
    color: ${theme.colors.gray[500]};
    font-size: 3rem;
  }
`;

const EditorWrapper = styled.div`
  border: 2px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  margin-top: ${theme.spacing.sm};
  background: ${theme.colors.gray[50]};
  padding: ${theme.spacing.md};
  
  .quill {
    border: none;
    background: white;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 3rem;
  color: ${theme.colors.eventTypes.design.primary};
`;

const DesignEventDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('id');
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      // First try MongoDB events
      const { data: mongoEvent } = await supabase
        .from('events')
        .select('*')
        .eq('_id', eventId)
        .single();

      if (mongoEvent) {
        setEvent({ ...mongoEvent, source: 'mongodb' });
      } else {
        // Try Supabase events
        const { data: supabaseEvent, error } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();

        if (error) throw error;
        
        setEvent({ ...supabaseEvent, _id: supabaseEvent.id, source: 'supabase' });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Impossible de charger les détails de l\'événement');
      setLoading(false);
    }
  };

  const getEventStatus = () => {
    if (!event) return 'unknown';
    
    const now = new Date();
    const startDate = new Date(event.inscriptions_start_date);
    const endDate = new Date(event.inscriptions_end_date);
    const eventDate = new Date(event.main_date || event.event_start_date);

    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'active';
    if (now > eventDate) return 'past';
    return 'closed';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non défini';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (amPm) => {
    if (amPm === 'am') return 'Matin (9h-13h)';
    if (amPm === 'pm') return 'Après-midi (14h-18h)';
    return 'Non défini';
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>⏳</LoadingSpinner>
      </Container>
    );
  }

  if (error || !event) {
    return (
      <Container>
        <PageHeader>
          <BackButton onClick={() => navigate("/admin/events")}>
            ← Retour aux événements
          </BackButton>
          <Title>Erreur</Title>
          <Subtitle>{error || 'Événement introuvable'}</Subtitle>
        </PageHeader>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate("/admin/events")}>
          ← Retour aux événements
        </BackButton>
        <Title>{event.title_fr}</Title>
        <Subtitle>Détails de l'événement Milan 2026</Subtitle>
      </PageHeader>

      <MainLayout>
        <FormContainer>
          {/* Basic Information */}
          <Card>
            <SectionHeader>
              <SectionIcon>🎨</SectionIcon>
              <SectionTitle>Informations générales</SectionTitle>
            </SectionHeader>
            
            <InfoGroup>
              <Label>Type d'événement</Label>
              <Value>Design Event - Milan 2026</Value>
            </InfoGroup>

            <InfoGroup>
              <Label>Titre français</Label>
              <Value>{event.title_fr}</Value>
            </InfoGroup>

            <InfoGroup>
              <Label>Titre anglais</Label>
              <Value>{event.title_en}</Value>
            </InfoGroup>

            <InfoGroup>
              <Label>Localisation</Label>
              <Value>{event.location || event.localisation || 'Non définie'}</Value>
            </InfoGroup>
          </Card>

          {/* Descriptions */}
          <Card>
            <SectionHeader>
              <SectionIcon>📄</SectionIcon>
              <SectionTitle>Descriptions</SectionTitle>
            </SectionHeader>

            <InfoGroup>
              <Label>Description française</Label>
              <EditorWrapper>
                <div dangerouslySetInnerHTML={{ __html: event.description_fr || 'Aucune description' }} />
              </EditorWrapper>
            </InfoGroup>

            <InfoGroup>
              <Label>Description anglaise</Label>
              <EditorWrapper>
                <div dangerouslySetInnerHTML={{ __html: event.description_en || 'Aucune description' }} />
              </EditorWrapper>
            </InfoGroup>
          </Card>

          {/* Dates */}
          <Card>
            <SectionHeader>
              <SectionIcon>📅</SectionIcon>
              <SectionTitle>Dates et horaires</SectionTitle>
            </SectionHeader>

            <InfoGroup>
              <Label>Date de l'événement physique</Label>
              <Value>{formatDate(event.main_date || event.event_start_date)}</Value>
            </InfoGroup>

            <InfoGroup>
              <Label>Date de l'événement en ligne</Label>
              <Value>{formatDate(event.online_date || event.event_end_date)}</Value>
            </InfoGroup>

            <InfoGroup>
              <Label>Horaires</Label>
              <Value>{formatTime(event.am_or_pm)}</Value>
            </InfoGroup>

            <InfoGroup>
              <Label>Période d'inscriptions</Label>
              <Value>
                Du {formatDate(event.inscriptions_start_date)} au {formatDate(event.inscriptions_end_date)}
              </Value>
            </InfoGroup>
          </Card>

          {/* Media */}
          <Card>
            <SectionHeader>
              <SectionIcon>🖼️</SectionIcon>
              <SectionTitle>Médias</SectionTitle>
            </SectionHeader>

            <MediaContainer>
              <MediaItem>
                <Label>Logo</Label>
                {event.logo_path ? (
                  <img 
                    src={event.source === 'supabase' 
                      ? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/event-images/${event.logo_path}`
                      : event.logo_path
                    } 
                    alt="Logo de l'événement" 
                  />
                ) : (
                  <div className="placeholder">📷</div>
                )}
              </MediaItem>

              <MediaItem>
                <Label>Visuel principal</Label>
                {event.image_path ? (
                  <img 
                    src={event.source === 'supabase'
                      ? `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/event-images/${event.image_path}`
                      : event.image_path
                    } 
                    alt="Visuel de l'événement" 
                  />
                ) : (
                  <div className="placeholder">🖼️</div>
                )}
              </MediaItem>
            </MediaContainer>
          </Card>
        </FormContainer>

        {/* Sidebar */}
        <PreviewCard>
          <PreviewTitle>📊 Informations clés</PreviewTitle>
          
          <InfoGroup>
            <Label>Statut</Label>
            <StatusBadge status={getEventStatus()}>
              {getEventStatus() === 'active' && 'Inscriptions ouvertes'}
              {getEventStatus() === 'upcoming' && 'À venir'}
              {getEventStatus() === 'past' && 'Terminé'}
              {getEventStatus() === 'closed' && 'Inscriptions fermées'}
            </StatusBadge>
          </InfoGroup>

          <InfoGroup>
            <Label>Source des données</Label>
            <Value>{event.source === 'supabase' ? 'Supabase' : 'MongoDB'}</Value>
          </InfoGroup>

          <InfoGroup>
            <Label>ID de l'événement</Label>
            <Value style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
              {event._id}
            </Value>
          </InfoGroup>

          <div style={{ marginTop: theme.spacing.xl }}>
            <ActionButton 
              onClick={() => navigate({
                pathname: "/admin/events/fd100projects-modern",
                search: `?eid=${event._id}`
              })}
            >
              Voir les candidatures →
            </ActionButton>
          </div>

          <div style={{ marginTop: theme.spacing.md }}>
            <ActionButton 
              onClick={() => navigate({
                pathname: "/tableau-demo"
              })}
              style={{
                background: `linear-gradient(135deg, ${theme.colors.eventTypes.design.dark} 0%, ${theme.colors.eventTypes.design.primary} 100%)`
              }}
            >
              Tableau détaillé →
            </ActionButton>
          </div>
        </PreviewCard>
      </MainLayout>
    </Container>
  );
};

export default DesignEventDetails;